import { Info, RotateCcw } from "lucide-react";
import React from "react";

type VerificationFailureProps = {
  variant: "id" | "media" | "domain" | "all";
  onRetry?: () => void;
  onReset?: () => void;
};

export default function VerificationFailure({
  variant,
  onRetry,
  onReset,
}: VerificationFailureProps) {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    onReset?.();
  };

  // ==========================================
  // SPECIAL "ALL" VARIANT (ENTER KEY PRESSED)
  // Combines Media & Domain failure into a split layout using Figma UI styles
  // ==========================================
  if (variant === "all") {
    return (
      <section className="verification-failure w-full bg-white text-black antialiased">
        <div className="mx-auto w-full max-w-[1100px] px-4 pb-8 pt-7 sm:px-5 sm:pt-8 md:px-[30px] md:pb-[34px] md:pt-[36px]">
          
          {/* Main Title */}
          <header className="flex flex-col items-center text-center mb-8 md:mb-12">
            <h1
              className="m-0 text-[#dc2323] text-[28px] md:text-[36px] font-bold"
              style={{ fontFamily: "'Caviar Dreams', sans-serif", letterSpacing: "0.5px" }}
            >
              Verification Failed!
            </h1>
          </header>

          {/* Side-by-Side Content Container */}
          <div className="flex flex-col md:flex-row gap-y-10 md:gap-x-12 px-2 md:px-6">
            
            {/* LEFT COLUMN: Media */}
            <div className="flex-1 flex flex-col">
              <div className="flex flex-col items-center text-center h-auto md:min-h-[110px]">
                <h2 
                  className="text-[20px] md:text-[22px] font-bold text-black mb-4" 
                  style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
                >
                  If Media Verification Failed
                </h2>
                <p className="font-bold text-[15px] text-black mb-1">Vastavik ID / NFT Search Failed</p>
                <p className="text-[14px] md:text-[15px] text-black">
                  We could not find a matching record for the provided Vastavik ID or NFT Token.
                </p>
              </div>

              <div className="h-px w-full bg-[#a3a3a3] my-6 md:my-8" />

              <div className="flex flex-col gap-8">
                <section>
                  <h3 
                    className="m-0 mb-[16px] text-left text-[#dc2323] text-[18px] md:text-[20px] font-bold" 
                    style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
                  >
                    Possible Reasons
                  </h3>
                  <ul className="m-0 list-outside list-disc pl-5 text-left text-black marker:text-black space-y-2 text-[14px] md:text-[15px]">
                    <li className="leading-relaxed">The provided Vastavik ID or NFT Token is incorrect.</li>
                    <li className="leading-relaxed">The associated media has not been validated on the Vastavik platform.</li>
                  </ul>
                </section>
                
                <section>
                  <h3 
                    className="m-0 mb-[16px] text-left text-[#00783f] text-[18px] md:text-[20px] font-bold" 
                    style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
                  >
                    What You Can Do
                  </h3>
                  <ul className="m-0 list-outside list-disc pl-5 text-left text-black marker:text-black space-y-2 text-[14px] md:text-[15px]">
                    <li className="leading-relaxed">Verify that the Vastavik ID or NFT Token was entered correctly.</li>
                    <li className="leading-relaxed">Check for any missing or incorrect characters.</li>
                    <li className="leading-relaxed">Confirm that the content creator has validated the media on Vastavik.</li>
                    <li className="leading-relaxed">Try searching again using the QR Code if one is available.</li>
                  </ul>
                </section>
              </div>
            </div>

            {/* MIDDLE VERTICAL DIVIDER */}
            <div className="hidden md:block w-px bg-[#d3d3d3]" aria-hidden="true" />
            <div className="block md:hidden h-px w-full bg-[#d3d3d3]" aria-hidden="true" />

            {/* RIGHT COLUMN: Website */}
            <div className="flex-1 flex flex-col">
              <div className="flex flex-col items-center text-center h-auto md:min-h-[110px]">
                <h2 
                  className="text-[20px] md:text-[22px] font-bold text-black mb-4" 
                  style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
                >
                  If Website Verification failed
                </h2>
                <p className="text-[14px] md:text-[15px] text-black">
                  We could not find a matching record for the provided domain name for the website on our database.
                </p>
              </div>

              <div className="h-px w-full bg-[#a3a3a3] my-6 md:my-8" />

              <div className="flex flex-col gap-8">
                <section>
                  <h3 
                    className="m-0 mb-[16px] text-left text-[#dc2323] text-[18px] md:text-[20px] font-bold" 
                    style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
                  >
                    Possible Reasons
                  </h3>
                  <ul className="m-0 list-outside list-disc pl-5 text-left text-black marker:text-black space-y-2 text-[14px] md:text-[15px]">
                    <li className="leading-relaxed">The provided domain name/website URL is incorrect or,</li>
                    <li className="leading-relaxed">The provided domain name may not be a verified one by our platform.</li>
                  </ul>
                </section>
                
                <section>
                  <h3 
                    className="m-0 mb-[16px] text-left text-[#00783f] text-[18px] md:text-[20px] font-bold" 
                    style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
                  >
                    What You Can Do
                  </h3>
                  <ul className="m-0 list-outside list-disc pl-5 text-left text-black marker:text-black space-y-2 text-[14px] md:text-[15px]">
                    <li className="leading-relaxed">
                      Found a Verified Website badge on a website but can't verify the website on our platform? Please report it using this form. <a href="#" className="text-[#dc2323] underline hover:text-[#a01616]">click here</a>
                    </li>
                  </ul>
                </section>
              </div>
            </div>

          </div>

          {/* Try Again Button */}
          {(onRetry || onReset) && (
            <div className="flex justify-center pt-10 md:pt-14">
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex min-h-[45px] w-full max-w-[220px] cursor-pointer items-center justify-center gap-[12px] rounded-[6px] border-0 bg-black px-[22px] py-[10px] text-white transition-all duration-150 ease-in hover:bg-[#202020] active:translate-y-px md:w-[180px] md:max-w-none font-medium"
              >
                <RotateCcw aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
                <span>Try Again</span>
              </button>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ==========================================
  // REGULAR VARIANTS (EXPLICIT BUTTON CLICKED)
  // ==========================================
  const isMedia = variant === "media" || variant === "id";
  const isDomain = variant === "domain";

  const title = isDomain ? "Website Verification Failed!" : "Verification Failed!";
  const context = isDomain ? null : "Media Verification Failed";

  const message = isDomain
    ? "We could not find a matching record for the provided domain name for the website on our database."
    : "We couldn't verify the uploaded media.";

  const reasons = isDomain
    ? [
        "The provided domain name/website URL is incorrect or,",
        "The provided domain name may not be a verified one by our platform.",
      ]
    : [
        "The media does not exist on the Vastavik platform because it has not been validated by its content creator.",
        "The uploaded media is of insufficient quality, blurred, cropped, or incomplete, preventing successful verification.",
      ];

  const whatYouCanDo = isDomain
    ? [
        <React.Fragment key="domain-report">
          Found a Verified Website badge on a website but can't verify the website on our platform? Please report it using this form.{" "}
          <a href="#" className="text-[#dc2323] underline hover:text-[#a01616]">
            click here
          </a>
        </React.Fragment>,
      ]
    : [
        "Check whether the media contains a Vastavik ID or QR Code.",
        "If available, verify the media using the Vastavik ID or by scanning the QR Code.",
        "Ensure the content creator has completed validation of the media on the Vastavik platform.",
        "Upload a higher-quality version of the media and try again.",
      ];

  return (
    <section className="verification-failure w-full bg-white text-black antialiased">
      <div className="mx-auto w-full max-w-[900px] px-4 pb-8 pt-7 sm:px-5 sm:pt-8 md:px-[30px] md:pb-[34px] md:pt-[36px]">
        {/* Heading */}
        <header className="flex flex-col items-center text-center">
          <h1
            className="m-0 text-[#dc2323] text-[28px] md:text-[36px] font-bold"
            style={{ fontFamily: "'Caviar Dreams', sans-serif", letterSpacing: "0.5px" }}
          >
            {title}
          </h1>

          {context && (
            <p className="mt-2 text-black text-[16px] font-medium">
              {context}
            </p>
          )}

          <p className="mx-auto mt-3 max-w-[760px] text-black text-[15px]">
            {message}
          </p>

          {/* Info Box matching image_89cf6f.png */}
          {isMedia && (
            <div className="mx-auto mt-5 inline-flex items-center text-left bg-[#f4f4f4] border border-[#d6d6d6] rounded-[4px] px-3 py-2 md:mt-6 w-auto max-w-[840px]">
              <Info
                aria-hidden="true"
                className="mr-2 h-[18px] w-[18px] shrink-0 text-[#555]"
                strokeWidth={1.8}
              />
              <span className="text-[13px] md:text-[14px] text-black">
                Only media that has been validated by its content creator on Vastavik can be verified on the platform.
              </span>
            </div>
          )}
        </header>

        {/* Main horizontal line */}
        <div className={`mt-[24px] h-px w-full bg-[#a3a3a3] md:mt-[28px]`} aria-hidden="true" />

        {/* Two-column information */}
        <div className={`grid grid-cols-1 gap-y-[30px] pt-[25px] md:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] md:gap-x-12 md:pt-[32px] px-2 md:px-6`}>
          <section className="min-w-0 p-0">
            <h2 
              className="m-0 mb-[16px] text-left text-[#dc2323] text-[20px] md:text-[22px] font-bold"
              style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
            >
              Possible Reasons
            </h2>

            <ul className="m-0 list-outside list-disc pl-5 text-left text-black marker:text-black space-y-2 text-[14px] md:text-[15px]">
              {reasons.map((reason, index) => (
                <li key={`reason-${index}`} className="pl-1 leading-relaxed">
                  {reason}
                </li>
              ))}
            </ul>
          </section>

          {/* Vertical Divider */}
          <div className="h-px min-h-px w-full bg-[#d3d3d3] md:h-auto md:min-h-[150px] md:w-px" aria-hidden="true" />

          <section className="min-w-0 p-0">
            <h2 
              className="m-0 mb-[16px] text-left text-[#00783f] text-[20px] md:text-[22px] font-bold"
              style={{ fontFamily: "'Caviar Dreams', sans-serif" }}
            >
              What You Can Do
            </h2>

            <ul className="m-0 list-outside list-disc pl-5 text-left text-black marker:text-black space-y-2 text-[14px] md:text-[15px]">
              {whatYouCanDo.map((item, index) => (
                <li key={`action-${index}`} className="pl-1 leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Try Again button */}
        {(onRetry || onReset) && (
          <div className={`flex justify-center pt-[40px] md:pt-[50px]`}>
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex min-h-[45px] w-full max-w-[220px] cursor-pointer items-center justify-center gap-[12px] rounded-[6px] border-0 bg-black px-[22px] py-[10px] text-white transition-all duration-150 ease-in hover:bg-[#202020] active:translate-y-px md:w-[180px] md:max-w-none font-medium"
            >
              <RotateCcw aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
              <span>Try Again</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}